import React, { useState } from "react";
import { generateGarminAuthUrl } from "../services/garminService";

interface GarminConnectProps {
  onConnectionStart?: () => void; // Callback when connection process starts
  onPopupBlocked?: () => void; // Callback when popup is blocked
  connectedDeviceCount?: number;
  maxDevices?: number;
  iconSrc?: string;
}

// Garmin connects direct (not via Terra's hosted widget) — this mirrors
// TerraConnect's popup pattern exactly, but there's no postMessage from the
// popup: Garmin's OAuth callback is a standalone backend page, not a PWA
// route, so completion is detected purely by WearableSettings' existing
// device-count polling (started via onConnectionStart), same as it already
// works for every other provider.
const GarminConnect: React.FC<GarminConnectProps> = ({
  onConnectionStart,
  onPopupBlocked,
  connectedDeviceCount = 0,
  maxDevices,
  iconSrc,
}) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const connectGarmin = async () => {
    if (maxDevices !== undefined && connectedDeviceCount >= maxDevices) {
      setErrorMessage(
        `Device limit reached. You can connect up to ${maxDevices} devices.`,
      );
      return;
    }

    setLoading(true);
    setErrorMessage("");

    // Open the popup immediately (iOS Safari compatibility), redirect once
    // we have the real URL.
    const popup = window.open("", "_blank");

    try {
      const { url } = await generateGarminAuthUrl();

      if (url) {
        if (!popup || popup.closed || typeof popup.closed === "undefined") {
          onPopupBlocked?.();
          setErrorMessage(
            "Popup was blocked. Please allow popups for this site and try again.",
          );
        } else {
          popup.location.href = url;
          onConnectionStart?.();
        }
      } else {
        popup?.close();
        setErrorMessage("Failed to generate Garmin auth URL");
      }
    } catch (error) {
      popup?.close();
      console.error("Error connecting to Garmin:", error);
      setErrorMessage(
        error instanceof Error ? error.message : "An unknown error occurred",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="garmin-connect">
      <button
        onClick={connectGarmin}
        disabled={
          loading || (maxDevices !== undefined && connectedDeviceCount >= maxDevices)
        }
        className={`w-full p-4 border-2 bg-base-300 text-center border-base-300 rounded-lg hover:border-base-content/60 hover:bg-base-200 transition-all items-center gap-3 ${
          maxDevices !== undefined && connectedDeviceCount >= maxDevices
            ? "opacity-50 cursor-not-allowed"
            : ""
        }`}
      >
        <div className="flex items-center gap-3">
          {iconSrc && !loading && (
            <img
              src={iconSrc}
              alt="Garmin"
              className="w-10 h-10 object-contain rounded-2xl shadow-lg"
            />
          )}
          {loading ? (
            <span className="loading loading-spinner loading-xs"></span>
          ) : (
            <span className="text-lg font-grotesk text-base-content pl-1">
              My device is a <strong>Garmin</strong>
            </span>
          )}
        </div>
      </button>

      {errorMessage && (
        <div className="mt-2 text-red-600 text-sm">{errorMessage}</div>
      )}
    </div>
  );
};

export default GarminConnect;
